// Helpers de identificacao de time/seat em 2v2.
// Equivalente ao findMe2v2 do vanilla.

import type { MatchSnapshot, PlayerSnapshot } from '../stores/match';

export type Seats2v2 = {
  meTeam: 'A' | 'B';
  me: PlayerSnapshot | null;
  partner: PlayerSnapshot | null;
  oppLeft: PlayerSnapshot | null;
  oppRight: PlayerSnapshot | null;
  teamMine: PlayerSnapshot[];
  teamOpp: PlayerSnapshot[];
  mineIds: string[];
};

// Layout: me em baixo, partner em cima (frente-a-frente), oponentes
// nas laterais. teamA = [A0, A1], teamB = [B0, B1]. SeatOrder canonico
// (backend) = [A0, B0, A1, B1].
//
// Sentido ANTI-HORARIO (regra do truco, Leonardo 2026-05-19):
// depois de quem joga vai pra DIREITA do jogador. Pra cada viewer, o
// proximo no seatOrder e renderizado em oppRight (direita visual), e
// o anterior em oppLeft. Sem isso, a sequencia visual era horario
// (bottom→left→top→right) — contrario a regra.
//
// Tabela de mapeamento (seatOrder [A0, B0, A1, B1]):
//   viewer=A0 (idx 0): next=B0 → oppRight, prev=B1 → oppLeft, partner=A1
//   viewer=B0 (idx 1): next=A1 → oppRight (esquerda da direita... — partner!),
//     espera, partner = idx+2 mod 4 = idx 3 = B1. opp do meu time, mas
//     opp dos meus oponentes (A0, A1) vao nas laterais.
//
// Como teamMine sao os 2 do meu time e teamOpp os 2 opostos, o opp
// proximo a jogar depois de mim e sempre quem vem +1 no seatOrder
// (que e do OUTRO time). Da pra simplificar: oppRight = unico opp
// na posicao (myIdx + 1) mod 4 do seatOrder.
//
// "Espectador" (id nao bate com nenhum): mostra teamA como "nos"
// como fallback.
export function findMe2v2(snap: MatchSnapshot, userId: string): Seats2v2 {
  const ua = snap.team_a || [];
  const ub = snap.team_b || [];

  // SeatOrder canon do backend: alterna A e B.
  const seatOrder: (PlayerSnapshot | null)[] = [
    ua[0] ?? null,
    ub[0] ?? null,
    ua[1] ?? null,
    ub[1] ?? null,
  ];
  const myIdx = seatOrder.findIndex((p) => p && p.id === userId);

  if (myIdx < 0) {
    // Espectador (ou snap parcial): fallback ao layout default.
    return {
      meTeam: 'A',
      me: null,
      partner: null,
      oppLeft: ub[0] ?? null,
      oppRight: ub[1] ?? null,
      teamMine: ua,
      teamOpp: ub,
      mineIds: ua.map((p) => p.id),
    };
  }

  // Sentido anti-horario: next no seatOrder = direita visual, prev = esquerda.
  const nextIdx = (myIdx + 1) % 4;
  const partnerIdx = (myIdx + 2) % 4;
  const prevIdx = (myIdx + 3) % 4;

  const me = seatOrder[myIdx];
  const partner = seatOrder[partnerIdx];
  const oppRight = seatOrder[nextIdx];
  const oppLeft = seatOrder[prevIdx];

  const meTeam: 'A' | 'B' = myIdx === 0 || myIdx === 2 ? 'A' : 'B';
  const teamMine = meTeam === 'A' ? ua : ub;
  const teamOpp = meTeam === 'A' ? ub : ua;

  return {
    meTeam,
    me,
    partner,
    oppLeft,
    oppRight,
    teamMine,
    teamOpp,
    mineIds: teamMine.map((p) => p.id),
  };
}
